import { useState } from 'react'
import { Box, ChartTip, Stack } from '@ui'
import { UNITS } from '../format.js'
import { niceTicks, useWidth } from './chartUtils.js'

// Distance through the year so far, against a straight target line from 0 on 1 January to the
// annual target on 31 December, and (for the current year) a projection from today at a chosen
// daily rate. One measure, one axis: miles (or km) walked to date.

const HEIGHT = 260
const MARGIN = { top: 14, right: 12, bottom: 28, left: 52 }
// Room for the direct labels to the right of the plot, when the chart is wide enough for them.
const LABEL_WIDTH = 104
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LABEL_GAP = 14

// model: from yearModel() in Targets.jsx. projection: { rate, end } in metres, or null.
export default function YearChart({ model, unit, projection }) {
  const [wrapRef, width] = useWidth()
  const [hoverDay, setHoverDay] = useState(null)
  const u = UNITS[unit].meters
  const { year, days, elapsed, cumulative, target, monthStarts } = model

  const top = Math.max(target ?? 0, projection?.end ?? 0, cumulative[elapsed - 1] ?? 0, u) * 1.04
  const labelled = width >= 560
  const innerW = width - MARGIN.left - MARGIN.right - (labelled ? LABEL_WIDTH : 0)
  const innerH = HEIGHT - MARGIN.top - MARGIN.bottom
  const x = (day) => MARGIN.left + (day / days) * innerW
  const y = (meters) => MARGIN.top + innerH - (meters / top) * innerH
  const yTicks = niceTicks(0, top / u, 4)

  // Actual: (end of day i, total to then), starting from 0 at the start of 1 January.
  let actualPath = elapsed > 0 ? `M${x(0)},${y(0)}` : ''
  for (let i = 1; i <= elapsed; i++) actualPath += `L${x(i).toFixed(1)},${y(cumulative[i - 1]).toFixed(1)}`
  const total = elapsed > 0 ? cumulative[elapsed - 1] : 0

  // Direct labels just right of the lines' ends, pushed apart if they would overlap. On narrow
  // screens the legend and tooltip carry them instead.
  const labels = []
  if (labelled && target) labels.push({ key: 'target', y: y(target), text: `Target ${fmt(target / u)}` })
  if (labelled && projection) labels.push({ key: 'projection', y: y(projection.end), text: `Projected ${fmt(projection.end / u)}` })
  labels.sort((a, b) => a.y - b.y)
  if (labels.length === 2 && labels[1].y - labels[0].y < LABEL_GAP) {
    const mid = (labels[0].y + labels[1].y) / 2
    labels[0].y = mid - LABEL_GAP / 2
    labels[1].y = mid + LABEL_GAP / 2
  }

  const narrow = innerW < 480
  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const day = Math.round(((e.clientX - rect.left - MARGIN.left) / innerW) * days)
    setHoverDay(day >= 1 && day <= days ? day : null)
  }

  const hover = hoverDay && hoverInfo(hoverDay, model, projection)

  return (
    <Box ref={wrapRef} position="relative">
      <Legend unit={unit} target={target} projection={projection} />
      <svg
        width={width} height={HEIGHT} role="img"
        aria-label={`Distance walked through ${year} against the target; the month table has the same figures`}
        onPointerMove={handleMove} onPointerLeave={() => setHoverDay(null)}
        style={{ touchAction: 'pan-y', display: 'block' }}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={MARGIN.left} x2={x(days)} y1={y(t * u)} y2={y(t * u)} stroke="var(--chart-grid)" strokeWidth="1" />
            <text x={MARGIN.left - 6} y={y(t * u)} dy="0.32em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 11 }}>
              {t.toLocaleString('en-GB')}
            </text>
          </g>
        ))}
        {monthStarts.map((start, m) => {
          const end = m < 11 ? monthStarts[m + 1] : days
          return (
            <g key={m}>
              {m > 0 && <line x1={x(start)} x2={x(start)} y1={MARGIN.top + innerH} y2={MARGIN.top + innerH + 4} stroke="var(--wl-muted)" />}
              <text x={x((start + end) / 2)} y={HEIGHT - 8} textAnchor="middle" fill="var(--wl-muted)" style={{ fontSize: 11 }}>
                {narrow ? MONTHS[m][0] : MONTHS[m]}
              </text>
            </g>
          )
        })}

        {target && (
          <line x1={x(0)} y1={y(0)} x2={x(days)} y2={y(target)}
            stroke="var(--wl-muted)" strokeWidth="1.5" strokeDasharray="5 4" />
        )}
        {projection && elapsed < days && (
          <line x1={x(elapsed)} y1={y(total)} x2={x(days)} y2={y(projection.end)}
            stroke="var(--chart-distance)" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" />
        )}
        {actualPath && (
          <path d={actualPath} fill="none" stroke="var(--chart-distance)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        )}
        {elapsed > 0 && elapsed < days && (
          <circle cx={x(elapsed)} cy={y(total)} r="4" fill="var(--chart-distance)" stroke="var(--wl-surface)" strokeWidth="2" />
        )}
        {labels.map((l) => (
          <text key={l.key} x={x(days) + 6} y={l.y} dy="0.32em"
            fill="var(--wl-text)" style={{ fontSize: 12 }}>
            {l.text}
          </text>
        ))}

        {hover && (
          <>
            <line x1={x(hoverDay)} x2={x(hoverDay)} y1={MARGIN.top} y2={MARGIN.top + innerH} stroke="var(--wl-muted)" strokeWidth="1" />
            {hover.actual != null && <circle cx={x(hoverDay)} cy={y(hover.actual)} r="4" fill="var(--chart-distance)" stroke="var(--wl-surface)" strokeWidth="2" />}
            {hover.projected != null && <circle cx={x(hoverDay)} cy={y(hover.projected)} r="4" fill="var(--wl-surface)" stroke="var(--chart-distance)" strokeWidth="2" />}
          </>
        )}
      </svg>

      {hover && (
        <ChartTip
          style={{ top: 30, left: Math.min(x(hoverDay) + 10, width - 200), pointerEvents: 'none', minWidth: 180 }}
        >
          <Box weight="semibold">{hover.date}</Box>
          {hover.actual != null && <div>Walked {fmt(hover.actual / u, 1)} {UNITS[unit].label}</div>}
          {hover.projected != null && <div>Projected {fmt(hover.projected / u, 1)} {UNITS[unit].label}</div>}
          {hover.target != null && <Box muted>Target to date {fmt(hover.target / u, 1)} {UNITS[unit].label}</Box>}
          {hover.target != null && hover.actual != null && (
            <div>{aheadText(hover.actual - hover.target, unit)}</div>
          )}
        </ChartTip>
      )}
    </Box>
  )
}

function hoverInfo(day, model, projection) {
  const { year, elapsed, cumulative, target, days } = model
  const date = new Date(Date.UTC(year, 0, day)).toLocaleDateString('en-GB', {
    timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short',
  })
  const info = { date, target: target ? (target * day) / days : null, actual: null, projected: null }
  if (day <= elapsed) info.actual = cumulative[day - 1]
  else if (projection) info.projected = (cumulative[elapsed - 1] ?? 0) + projection.rate * (day - elapsed)
  return info
}

function Legend({ unit, target, projection }) {
  const swatch = (dash, color) => (
    <Box as="svg" width="22" height="10" aria-hidden="true" me={1}>
      <line x1="1" x2="21" y1="5" y2="5" stroke={color} strokeWidth="2" strokeDasharray={dash} strokeLinecap="round" />
    </Box>
  )
  return (
    <Stack wrap gap={3} text="small" muted mb={1}>
      <span>{swatch(undefined, 'var(--chart-distance)')}Walked</span>
      {target && <span>{swatch('5 4', 'var(--wl-muted)')}Target pace</span>}
      {projection && (
        <span>{swatch('6 4', 'var(--chart-distance)')}At {fmt(projection.rate / UNITS[unit].meters, 1)} {UNITS[unit].label} a day from tomorrow</span>
      )}
    </Stack>
  )
}

export function fmt(value, digits = 0) {
  return value.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function aheadText(meters, unit, digits = 1) {
  const v = Math.abs(meters) / UNITS[unit].meters
  if (v < 0.05) return 'On target'
  return `${fmt(v, digits)} ${UNITS[unit].label} ${meters > 0 ? 'ahead' : 'behind'}`
}
